import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { TextField, Button, Box, Stack } from '@mui/material'
import type { CreateDictionaryData, UpdateDictionaryData, Dictionary } from '@/types'

const createDictionarySchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, t('dictionaries.validation.nameRequired'))
      .max(255, t('dictionaries.validation.nameMaxLength')),
    description: z
      .string()
      .max(1000, t('dictionaries.validation.descriptionMaxLength'))
      .optional()
      .or(z.literal('')),
  })

type DictionaryFormData = CreateDictionaryData

interface DictionaryFormProps {
  initialValues?: Dictionary
  onSubmit: (data: CreateDictionaryData | UpdateDictionaryData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function DictionaryForm({
  initialValues,
  onSubmit,
  onCancel,
  isLoading,
}: DictionaryFormProps) {
  const { t } = useTranslation()
  const isEdit = !!initialValues

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DictionaryFormData>({
    resolver: zodResolver(createDictionarySchema(t)),
    defaultValues: initialValues
      ? {
          name: initialValues.name,
          description: initialValues.description ?? '',
        }
      : { name: '', description: '' },
  })

  const handleFormSubmit = async (data: DictionaryFormData) => {
    const payload = isEdit
      ? ({ name: data.name, description: data.description || undefined } as UpdateDictionaryData)
      : ({ name: data.name, description: data.description || undefined } as CreateDictionaryData)
    await onSubmit(payload)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3} sx={{ pt: 1 }}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('dictionaries.form.name')}
              placeholder={t('dictionaries.form.namePlaceholder')}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              required
              size="small"
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('dictionaries.form.description')}
              placeholder={t('dictionaries.form.descriptionPlaceholder')}
              error={!!errors.description}
              helperText={errors.description?.message}
              fullWidth
              multiline
              rows={4}
              size="small"
            />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isLoading} size="small">
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            size="small"
          >
            {isEdit ? t('common.save') : t('dictionaries.create')}
          </Button>
        </Box>
      </Stack>
    </form>
  )
}
